import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Linkedin, Instagram, Mail } from "lucide-react";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-background border-t border-primary/10 overflow-hidden"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-24 pb-12">
        {/* CTA Row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 pb-20 border-b border-primary/10">
          <div className="lg:col-span-7">
            <div className="overline mb-6">Let's Build the Future</div>
            <h2 className="font-serif text-4xl md:text-6xl text-foreground font-light leading-[1.05] text-balance">
              Iconic developments begin with a single conversation.
            </h2>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end items-end">
            <Link
              to="/contact"
              data-testid="footer-cta-contact"
              className="group inline-flex items-center gap-4 text-primary border border-primary/40 px-8 py-5 hover:bg-primary hover:text-primary-foreground transition-all duration-500"
            >
              <span className="text-[13px] uppercase tracking-[0.25em]">
                Initiate Conversation
              </span>
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-10 py-16">
          <div className="col-span-2 lg:col-span-2">
            <Link to="/" className="font-serif text-3xl tracking-tight">ELLIOTT</Link>
            <p className="mt-4 text-sm text-muted-foreground leading-relaxed max-w-xs">
              Elliott International Development LLC — Tampa-based developer of
              landmark mixed-use towers, ultra-luxury residences, hospitality
              and urban transformations.
            </p>
            <div className="mt-6 font-mono text-xs text-foreground/60 space-y-1">
              <div>410 S Ware Blvd</div>
              <div>Tampa, Florida 33619</div>
              <div className="pt-2 text-primary">contact@elliottdev.com</div>
            </div>
          </div>

          <div>
            <div className="overline mb-5">Company</div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/about" className="text-foreground/80 hover:text-primary">About</Link></li>
              <li><Link to="/elliott-towers" className="text-foreground/80 hover:text-primary">Elliott Towers</Link></li>
              <li><Link to="/app-ecosystem" className="text-foreground/80 hover:text-primary">Ecosystem</Link></li>
            </ul>
          </div>

          <div>
            <div className="overline mb-5">Capital</div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/investor-relations" className="text-foreground/80 hover:text-primary">Investor Relations</Link></li>
              <li><Link to="/login" className="text-foreground/80 hover:text-primary">Investor Portal</Link></li>
              <li><Link to="/investor-relations#opportunities" className="text-foreground/80 hover:text-primary">Opportunities</Link></li>
              <li><Link to="/investor-relations#faq" className="text-foreground/80 hover:text-primary">FAQ</Link></li>
            </ul>
          </div>

          <div>
            <div className="overline mb-5">Press</div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/media" className="text-foreground/80 hover:text-primary">Newsroom</Link></li>
              <li><Link to="/media" className="text-foreground/80 hover:text-primary">Releases</Link></li>
              <li><Link to="/contact" className="text-foreground/80 hover:text-primary">Media Contact</Link></li>
            </ul>
          </div>

          <div>
            <div className="overline mb-5">Connect</div>
            <div className="flex gap-3">
              <a href="https://www.linkedin.com/company/elliott-international-development/" target="_blank" rel="noreferrer" data-testid="social-linkedin" className="w-10 h-10 border border-primary/30 flex items-center justify-center text-primary/80 hover:bg-primary hover:text-primary-foreground transition-colors"><Linkedin className="w-4 h-4" /></a>
              <a href="https://www.instagram.com/elliottdevelopment" target="_blank" rel="noreferrer" data-testid="social-instagram" className="w-10 h-10 border border-primary/30 flex items-center justify-center text-primary/80 hover:bg-primary hover:text-primary-foreground transition-colors"><Instagram className="w-4 h-4" /></a>
              <a href="mailto:contact@elliottdev.com" data-testid="social-email" className="w-10 h-10 border border-primary/30 flex items-center justify-center text-primary/80 hover:bg-primary hover:text-primary-foreground transition-colors"><Mail className="w-4 h-4" /></a>
            </div>
          </div>
        </div>

        {/* Massive wordmark */}
        <div className="pt-12 pb-6 overflow-hidden">
          <div className="font-serif tracking-[-0.02em] text-foreground/[0.06] leading-none select-none" style={{ fontSize: "min(28vw, 360px)" }}>
            ELLIOTT
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 pt-8 border-t border-primary/10">
          <div className="text-xs font-mono text-muted-foreground">
            © {new Date().getFullYear()} Elliott International Development LLC. All rights reserved.
          </div>
          <div className="flex gap-6 text-xs font-mono text-muted-foreground">
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-primary">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms" className="hover:text-primary">Terms</Link>
            <Link to="/contact" className="hover:text-primary">Accessibility</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
